import { api } from './client';
import {
  adminUserSchema,
  auditEntrySchema,
  keysetSchema,
  maintenanceOverviewSchema,
  needsReauthSchema,
  purgeRunSchema,
  releaseResultSchema,
  rotationSchema,
  subscriptionViewSchema,
  vaultSchema,
  withPasswordSchema,
  type AdminUser,
  type AuditEntry,
  type KeysetPage,
  type MaintenanceOverview,
  type NeedsReauthAccount,
  type PurgeRun,
  type ReleaseResult,
  type Rotation,
  type SubscriptionView,
  type Vault,
  type WithPassword,
} from './opsTypes';
import { pageSchema, type AdminRole, type Page } from './types';

// --- Maintenance -----------------------------------------------------------

export async function fetchMaintenance(): Promise<MaintenanceOverview> {
  return maintenanceOverviewSchema.parse(await api.get<unknown>('/maintenance'));
}

export async function releaseQueue(slug: string): Promise<ReleaseResult> {
  return releaseResultSchema.parse(await api.post<unknown>(`/maintenance/queues/${slug}/release`));
}

const subscriptionPageSchema = pageSchema(subscriptionViewSchema);

export async function listSubscriptions(
  params: { status?: string; page?: number } = {},
): Promise<Page<SubscriptionView>> {
  return subscriptionPageSchema.parse(
    await api.get<unknown>('/maintenance/subscriptions', { query: { ...params } }),
  );
}

export async function pauseSubscription(id: string, reason?: string): Promise<SubscriptionView> {
  return subscriptionViewSchema.parse(
    await api.post<unknown>(
      `/maintenance/subscriptions/${id}/pause`,
      reason ? { reason } : undefined,
    ),
  );
}

export async function resumeSubscription(id: string): Promise<SubscriptionView> {
  return subscriptionViewSchema.parse(
    await api.post<unknown>(`/maintenance/subscriptions/${id}/resume`),
  );
}

const needsReauthPageSchema = keysetSchema(needsReauthSchema);
const purgeRunPageSchema = keysetSchema(purgeRunSchema);

export interface Cursor {
  after: string;
  after_id: string;
}

export async function listNeedsReauth(cursor?: Cursor): Promise<KeysetPage<NeedsReauthAccount>> {
  return needsReauthPageSchema.parse(
    await api.get<unknown>('/maintenance/needs-reauth', { query: { ...cursor } }),
  );
}

export async function listPurgeRuns(job?: string, cursor?: Cursor): Promise<KeysetPage<PurgeRun>> {
  return purgeRunPageSchema.parse(
    await api.get<unknown>('/maintenance/purge-runs', { query: { job, ...cursor } }),
  );
}

export async function fetchVault(): Promise<Vault> {
  return vaultSchema.parse(await api.get<unknown>('/maintenance/vault'));
}

export async function rotateVault(fromKeyVersion: string, confirm: string): Promise<Rotation> {
  return rotationSchema.parse(
    await api.post<unknown>('/maintenance/vault/rotate', {
      from_key_version: fromKeyVersion,
      confirm,
    }),
  );
}

// --- Audit -----------------------------------------------------------------

export interface AuditFilters {
  actor_type?: string;
  action?: string;
  from?: string;
  to?: string;
  partner_id?: string;
  org_id?: string;
}

const auditPageSchema = keysetSchema(auditEntrySchema);

export async function listAudit(
  filters: AuditFilters,
  cursor?: Cursor,
): Promise<KeysetPage<AuditEntry>> {
  return auditPageSchema.parse(
    await api.get<unknown>('/audit', { query: { ...filters, ...cursor } }),
  );
}

export async function listAuditActions(): Promise<string[]> {
  return (await api.get<string[]>('/audit/actions')) ?? [];
}

// --- Admin users -----------------------------------------------------------

const adminUserPageSchema = pageSchema(adminUserSchema);

export async function listAdminUsers(page = 0): Promise<Page<AdminUser>> {
  return adminUserPageSchema.parse(await api.get<unknown>('/admin-users', { query: { page } }));
}

export async function createAdminUser(request: {
  email: string;
  name: string | null;
  role: AdminRole;
}): Promise<WithPassword> {
  return withPasswordSchema.parse(await api.post<unknown>('/admin-users', request));
}

export async function setAdminRole(id: string, role: AdminRole): Promise<AdminUser> {
  return adminUserSchema.parse(await api.post<unknown>(`/admin-users/${id}/role`, { role }));
}

export async function setAdminStatus(
  id: string,
  status: 'ACTIVE' | 'DISABLED',
): Promise<AdminUser> {
  return adminUserSchema.parse(await api.post<unknown>(`/admin-users/${id}/status`, { status }));
}

export async function resetAdminPassword(id: string): Promise<WithPassword> {
  return withPasswordSchema.parse(await api.post<unknown>(`/admin-users/${id}/password`));
}
